import { Copy, Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { API_BASE, apiFetch } from "../api";
import { lastSignOut } from "../auth";
import { relativeDate, shortDate } from "./format";

interface TokenRow {
  id: string;
  name: string;
  scope: "device" | "backup" | "mcp" | "feedback";
  created: number;
  last_used: number;
  method?: "password" | "google" | "passkey";
  /** Where it was made and last used ("Sydney, AU"); empty for tokens from before chat 11. */
  place?: string;
  last_place?: string;
}

const METHOD = { password: "Password", google: "Google", passkey: "Passkey" } as const;
const KIND = { backup: "Backup (read-only)", mcp: "Claude (MCP)", feedback: "Feedback (repo)" } as const;

/** "Password sign-in Oct 10 · Sydney, AU" / "Claude (MCP) · made Oct 1": how to tell same-named devices apart. */
function detail(t: TokenRow): string {
  const when = shortDate(t.created);
  const where = t.place ? ` · ${t.place}` : "";
  return t.scope === "device" ? `${METHOD[t.method ?? "password"]} sign-in ${when}${where}` : `${KIND[t.scope]} · made ${when}${where}`;
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await apiFetch(path, init);
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json() as Promise<T>;
}

/** Signed-in devices and scoped tokens (backup, Claude). Revoking signs that device out. */
export function DevicesDialog({ onClose }: { onClose: () => void }) {
  const [rows, setRows] = useState<TokenRow[] | null>(null);
  const [current, setCurrent] = useState("");
  const [error, setError] = useState("");
  const [created, setCreated] = useState<{ token: string; scope: "backup" | "mcp" | "feedback" } | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [idleDays, setIdleDays] = useState(90);

  const load = () =>
    api<{ tokens: TokenRow[]; current: string; idleDays?: number }>("/auth/tokens")
      .then((r) => {
        setRows(r.tokens);
        setCurrent(r.current);
        if (r.idleDays) setIdleDays(r.idleDays);
      })
      .catch(() => setError("Needs a connection to the server."));
  useEffect(() => void load(), []);
  const rename = async () => {
    if (!renaming) return;
    const name = renaming.name.trim();
    setRenaming(null);
    if (!name || name === rows?.find((r) => r.id === renaming.id)?.name) return;
    await api(`/auth/tokens/${renaming.id}`, { method: "PATCH", body: JSON.stringify({ name }) }).catch(() => setError("Couldn't rename it. Try again with a connection."));
    void load();
  };
  const newToken = async (scope: "backup" | "mcp" | "feedback", ask: string, initial: string) => {
    const name = prompt(ask, initial);
    if (!name) return;
    const r = await api<{ token: string }>("/auth/tokens", { method: "POST", body: JSON.stringify({ name, scope }) });
    setCreated({ token: r.token, scope });
    void load();
  };

  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal wide devices">
        <h2>Devices &amp; tokens</h2>
        {error && <p className="error-text">{error}</p>}
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th className="col-date">Last used</th>
              <th className="col-actions" />
            </tr>
          </thead>
          <tbody>
            {rows?.map((t) => (
              <tr key={t.id}>
                <td>
                  {renaming?.id === t.id ? (
                    <input
                      className="input device-name-input"
                      value={renaming.name}
                      maxLength={100}
                      autoFocus
                      aria-label="Device name"
                      onChange={(e) => setRenaming({ id: t.id, name: e.target.value })}
                      onBlur={() => void rename()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                        if (e.key === "Escape") {
                          e.stopPropagation();
                          setRenaming(null);
                        }
                      }}
                    />
                  ) : (
                    <button className="device-name" title="Rename" onClick={() => setRenaming({ id: t.id, name: t.name })}>
                      {t.name}
                      <Pencil size={12} className="device-name-pen" />
                    </button>
                  )}
                  {t.id === current && <span className="chip small">This device</span>}
                  <div className="device-detail">{detail(t)}</div>
                  {/* Phones hide the Last used column. */}
                  <div className="device-detail device-last">
                    Last used {relativeDate(t.last_used).replace(/^(Just now|Yesterday)$/, (w) => w.toLowerCase())}
                    {t.last_place ? ` · ${t.last_place}` : ""}
                  </div>
                </td>
                <td className="col-date muted">
                  {relativeDate(t.last_used)}
                  {t.last_place && <div className="device-detail">{t.last_place}</div>}
                </td>
                <td className="col-actions">
                  {t.id !== current && (
                    <button
                      className="icon-btn"
                      title="Revoke"
                      onClick={async () => {
                        if (!confirm(`Revoke "${t.name}"? It will be signed out.`)) return;
                        await api(`/auth/tokens/${t.id}`, { method: "DELETE" });
                        void load();
                      }}
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="small muted">Tap a name to rename it. Devices unused for {idleDays} days are signed out by themselves.</p>
        {created?.scope === "backup" && (
          <div className="token-box">
            <p className="small">Copy this backup token now; it won't be shown again. Put it in <code>~/engram-backup/env</code> on the Mac Mini.</p>
            <div className="token-row">
              <code>{created.token}</code>
              <button className="btn small" onClick={() => void navigator.clipboard.writeText(created.token)}>
                <Copy size={13} /> Copy
              </button>
            </div>
          </div>
        )}
        {created?.scope === "mcp" && <ClaudeSetup token={created.token} />}
        {created?.scope === "feedback" && <FeedbackSetup token={created.token} />}
        {!created && <ConnectClaude />}
        {!created && (
          <div className="token-actions">
            <button className="btn" onClick={() => void newToken("backup", "Name for the backup token", "Mac Mini backup")}>
              <Plus size={14} /> New backup token
            </button>
            <button className="btn" onClick={() => void newToken("mcp", "Name for the Claude token", "Claude")}>
              <Plus size={14} /> New Claude token
            </button>
            <button className="btn" onClick={() => void newToken("feedback", "Name for the feedback token", "Feedback to repo")}>
              <Plus size={14} /> New feedback token
            </button>
          </div>
        )}
        <LastSignIn />
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const SIGN_OUT_REASONS: Record<string, string> = {
  "no-cookie": "the browser no longer had its sign-in cookie",
  "no-token": "the app had no sign-in token",
  "unknown-token": "the server didn't know this device's token",
  revoked: "this device was revoked",
  expired: "this device hadn't been used for 90 days",
  "signed-out": "you signed out",
  "offline-first-run": "the server was unreachable on a first start",
};

/** Why this device last showed the sign-in screen, so an unexpected sign-out can be diagnosed. */
function LastSignIn() {
  const rec = lastSignOut();
  if (!rec) return null;
  return (
    <p className="small muted">
      This device last asked for sign-in {new Date(rec.at).toLocaleString()}: {SIGN_OUT_REASONS[rec.reason] ?? rec.reason}
      {rec.status ? ` (${rec.status})` : ""}.
    </p>
  );
}

/** The feedback token goes in a gitignored file that tools/feedback/pull.mjs reads (shown once). */
function FeedbackSetup({ token }: { token: string }) {
  const cmd = String.raw`Set-Content -Path C:\dev\engram\tools\feedback\.token -Value ` + token;
  return (
    <div className="token-box">
      <p className="small">Run this once in PowerShell on the PC. It saves the token where Claude's feedback tool reads it (the file is never committed). Shown only now.</p>
      <div className="token-row">
        <code>{cmd}</code>
        <button className="btn small" onClick={() => void navigator.clipboard.writeText(cmd)}>
          <Copy size={13} /> Copy
        </button>
      </div>
    </div>
  );
}

/**
 * Claude's connector (server oauth.ts): one address, then Connect and sign in with Google. Added on claude.ai it's in
 * the Claude apps on every device too; Claude Code signs in the same way. A pasted token (below) is the fallback.
 */
function ConnectClaude() {
  const url = `${API_BASE || location.origin}/api/mcp`;
  const cli = `claude mcp add --transport http engram ${url}`;
  return (
    <div className="token-box">
      <p className="small">
        <strong>Connect Claude</strong>: in Claude (claude.ai, or the app on the Mac or phone) open Settings › Connectors › Add custom connector, paste this address, then Connect and sign in with Google. Claude can then search, read, create and change your notes and tasks.
      </p>
      <div className="token-row">
        <code>{url}</code>
        <button className="btn small" onClick={() => void navigator.clipboard.writeText(url)}>
          <Copy size={13} /> Copy
        </button>
      </div>
      <p className="small">Claude Code: run this, then <code>/mcp</code> in Claude Code to sign in.</p>
      <div className="token-row">
        <code>{cli}</code>
        <button className="btn small" onClick={() => void navigator.clipboard.writeText(cli)}>
          <Copy size={13} /> Copy
        </button>
      </div>
    </div>
  );
}

/** How to connect Claude to Engram with a new MCP token (shown once). */
function ClaudeSetup({ token }: { token: string }) {
  const url = `${API_BASE || location.origin}/api/mcp`;
  const cli = `claude mcp add --transport http engram ${url} --header "Authorization: Bearer ${token}"`;
  const desktop = JSON.stringify({ mcpServers: { engram: { command: "npx", args: ["-y", "mcp-remote", url, "--header", `Authorization: Bearer ${token}`] } } }, null, 2);
  return (
    <div className="token-box">
      <p className="small">
        Copy this now; the token won't be shown again. It lets Claude search, read, create and edit your notes and tasks (nothing else). Revoke it here any time.
      </p>
      <p className="small"><strong>Claude Code</strong> (one command in a terminal):</p>
      <div className="token-row">
        <code>{cli}</code>
        <button className="btn small" onClick={() => void navigator.clipboard.writeText(cli)}>
          <Copy size={13} /> Copy
        </button>
      </div>
      <p className="small"><strong>Claude Desktop</strong> (Settings › Developer › Edit config, then restart):</p>
      <div className="token-row">
        <code className="pre">{desktop}</code>
        <button className="btn small" onClick={() => void navigator.clipboard.writeText(desktop)}>
          <Copy size={13} /> Copy
        </button>
      </div>
    </div>
  );
}
